import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import JsonLd from "../seo/json-ld";

const renderItems = (items = []) =>
  items.map((item) => (
    <div key={item.title || item.label || item.href} className="col-lg-4 col-md-6 mb-4">
      <article className="industry-card">
        <h3>{item.title || item.label}</h3>
        {item.text ? <p>{item.text}</p> : null}
        {item.href ? (
          <Link to={item.href} className="industry-inline-link">
            {item.linkLabel || "Learn more"}
          </Link>
        ) : null}
      </article>
    </div>
  ));

const renderLinks = (items = [], cardClassName = "industry-link-card") =>
  items.map((item) => (
    <div key={item.href} className="col-lg-4 col-md-6 mb-4">
      <Link to={item.href} className={cardClassName}>
        <h3>{item.label}</h3>
        <p>{item.text}</p>
      </Link>
    </div>
  ));

const SectionHeading = ({ eyebrow, title, lead }) => (
  <div className="industry-section-heading">
    {eyebrow ? <p className="section-eyebrow">{eyebrow}</p> : null}
    {title ? <h2 className="section-title">{title}</h2> : null}
    {lead ? <p className="section-sub">{lead}</p> : null}
  </div>
);

const EnterpriseServicePage = ({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  hero,
  whatItIs,
  whoItIsFor,
  businessProblems,
  scenarios,
  deliveryApproach,
  industries,
  relatedProducts,
  relatedServices,
  aiOpportunities,
  framework,
  cta,
  schemas = [],
}) => (
  <React.Fragment>
    <Helmet defer={false}>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
    </Helmet>
    <JsonLd data={schemas} />

    <main className="industry-page service-page">
      <section className="industry-hero" aria-labelledby="service-hero-title">
        <div className="container">
          <div className="industry-hero-grid">
            <div className="industry-hero-copy">
              <span className="section-eyebrow">{hero.eyebrow}</span>
              <h1 id="service-hero-title">{hero.heading}</h1>
              <p className="industry-hero-lead">{hero.lead}</p>
              <div className="industry-hero-tags" aria-label={`${hero.heading} focus areas`}>
                {hero.tags.map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
              <div className="industry-hero-actions">
                <Link to={hero.primaryCta.href} className="hero-v2-btn-primary">
                  {hero.primaryCta.label}
                </Link>
                <Link to={hero.secondaryCta.href} className="hero-v2-btn-outline">
                  {hero.secondaryCta.label}
                </Link>
              </div>
            </div>

            <div
              className="industry-hero-panel"
              style={{ "--industry-hero-image": `url(${hero.image})` }}
              aria-hidden="true"
            >
              <div className="industry-hero-panel-scrim" />
              <div className="industry-hero-panel-inner">
                <p className="industry-hero-panel-kicker">{hero.panelKicker}</p>
                <h2>{hero.panelTitle}</h2>
                <p>{hero.panelText}</p>
                {hero.stats ? (
                  <div className="industry-hero-stats">
                    {hero.stats.map((stat) => (
                      <div key={stat.label} className="industry-hero-stat">
                        <strong>{stat.value}</strong>
                        <span>{stat.label}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="industry-section">
        <div className="container">
          <SectionHeading eyebrow={whatItIs.eyebrow} title={whatItIs.title} lead={whatItIs.lead} />
          <div className="row align-items-stretch">
            <div className="col-lg-7 mb-4">
              <div className="industry-copy-card">
                {whatItIs.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </div>
            <div className="col-lg-5 mb-4">
              <div className="industry-info-card industry-info-card--accent">
                <h3>{whoItIsFor.title}</h3>
                <p>{whoItIsFor.lead}</p>
                <ul className="industry-info-list">
                  {whoItIsFor.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="industry-section industry-section--muted">
        <div className="container">
          <SectionHeading
            eyebrow={businessProblems.eyebrow}
            title={businessProblems.title}
            lead={businessProblems.lead}
          />
          <div className="row">{renderItems(businessProblems.items)}</div>
        </div>
      </section>

      <section className="industry-section">
        <div className="container">
          <SectionHeading eyebrow={scenarios.eyebrow} title={scenarios.title} lead={scenarios.lead} />
          <div className="row">
            {renderItems(
              scenarios.items.map((item) => ({
                title: item.title,
                text: item.text,
              }))
            )}
          </div>
        </div>
      </section>

      <section className="industry-section industry-section--muted">
        <div className="container">
          <div className="industry-split">
            <div>
              <SectionHeading
                eyebrow={deliveryApproach.eyebrow}
                title={deliveryApproach.title}
                lead={deliveryApproach.lead}
              />
              <div className="industry-copy-card">
                {deliveryApproach.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </div>
            <div className="industry-info-card">
              <h3>{deliveryApproach.callout.title}</h3>
              <ul className="industry-info-list">
                {deliveryApproach.callout.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="industry-section">
        <div className="container">
          <SectionHeading eyebrow={industries.eyebrow} title={industries.title} lead={industries.lead} />
          <div className="row">{renderLinks(industries.items)}</div>
        </div>
      </section>

      {framework ? (
        <section className="industry-section industry-section--muted">
          <div className="container">
            <SectionHeading eyebrow={framework.eyebrow} title={framework.title} lead={framework.lead} />
            <div className="row">
              {framework.items.map((item) => (
                <div key={item.title} className="col-lg-3 col-md-6 mb-4">
                  <article className="industry-card">
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="industry-section industry-section--muted">
        <div className="container">
          <SectionHeading
            eyebrow={aiOpportunities.eyebrow}
            title={aiOpportunities.title}
            lead={aiOpportunities.lead}
          />
          <div className="row">
            {renderItems(
              aiOpportunities.items.map((item) => ({
                title: item.title,
                text: item.text,
                href: item.href,
                linkLabel: item.linkLabel,
              }))
            )}
          </div>
        </div>
      </section>

      {relatedProducts ? (
        <section className="industry-section">
          <div className="container">
            <SectionHeading
              eyebrow={relatedProducts.eyebrow}
              title={relatedProducts.title}
              lead={relatedProducts.lead}
            />
            <div className="row">{renderLinks(relatedProducts.items)}</div>
          </div>
        </section>
      ) : null}

      {relatedServices ? (
        <section className="industry-section industry-section--muted">
          <div className="container">
            <SectionHeading
              eyebrow={relatedServices.eyebrow}
              title={relatedServices.title}
              lead={relatedServices.lead}
            />
            <div className="row">{renderLinks(relatedServices.items)}</div>
          </div>
        </section>
      ) : null}

      <section className="industry-cta">
        <div className="container">
          <div className="industry-cta-panel">
            <div>
              <p className="section-eyebrow">{cta.eyebrow}</p>
              <h2>{cta.title}</h2>
              <p>{cta.text}</p>
            </div>
            <div className="industry-cta-actions">
              <Link to={cta.primary.href} className="hero-v2-btn-primary">
                {cta.primary.label}
              </Link>
              <Link to={cta.secondary.href} className="hero-v2-btn-outline">
                {cta.secondary.label}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  </React.Fragment>
);

export default EnterpriseServicePage;
